import clsx from "clsx";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Cabecera y cuerpo de una pantalla (secciones 8.1 y 8.4).
 *
 * UN SOLO `h1` POR PANTALLA
 *   El título vive aquí, en Barlow Condensed y en versales, y las pantallas no escriben el suyo.
 *   Así no hay dos `h1` ni saltos de nivel cuando una pantalla crece.
 *
 * LA CABECERA ES PEGAJOSA
 *   A 360 px, con la lista larga, saber dónde estás cuesta un scroll entero hasta arriba. La
 *   cabecera se queda arriba con el fondo difuminado; las barras de filtro de M8 se pegan justo
 *   debajo, a 52 px, que es lo que mide.
 *
 * EL CUERPO DEJA SITIO A LAS PESTAÑAS
 *   `pb-tabs` reserva el alto de la barra inferior más el área segura: el final de una lista
 *   nunca queda debajo de las pestañas.
 */

export interface PageProps {
  title: string;
  /** Botón o enlace a la derecha del título. Solo uno: la acción primaria de la pantalla. */
  action?: ReactNode;
  /** Muestra el botón atrás. Lo usan las rutas completas, como la ficha de jugador. */
  back?: boolean;
  children: ReactNode;
  /** Contenido pegado a la cabecera y sin márgenes: el campo a sangre de Inicio. */
  bleed?: ReactNode;
  /** Barra pegajosa bajo la cabecera: los filtros del mercado. */
  sticky?: ReactNode;
  className?: string;
}

export function Page({
  title,
  action,
  back = false,
  children,
  bleed,
  sticky,
  className,
}: PageProps) {
  const navigate = useNavigate();

  return (
    <div className="pb-tabs mx-auto min-h-screen max-w-content">
      <header className="sticky top-0 z-20 flex min-h-[52px] items-center gap-2.5 border-b border-border bg-paper/90 px-3.5 py-2 backdrop-blur">
        {back ? (
          <button
            type="button"
            aria-label="Volver"
            onClick={() => navigate(-1)}
            className="-ml-2.5 flex h-touch w-touch shrink-0 items-center justify-center text-xl text-ink"
          >
            <span aria-hidden="true">‹</span>
          </button>
        ) : null}
        <h1 className="min-w-0 flex-1 truncate text-[23px] leading-tight tracking-[0.03em]">
          {title}
        </h1>
        {action}
      </header>

      {bleed}
      {sticky}

      <div className={clsx("grid gap-3 p-3.5 md:p-[18px]", className)}>
        {children}
      </div>
    </div>
  );
}
